
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

// Helper function to generate a unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Message types
export type MessageType = 'general' | 'resume' | 'linkedin' | 'highschool';

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  type: 'user' | 'assistant';
}

export interface AIAgent {
  id: string;
  name: string;
  photo_url: string | null;
  background: string;
  personality: string;
  expertise: string | null;
  is_default: boolean;
  elevenlabs_voice_id: string | null;
}

// Local storage keys
const PENDING_MESSAGE_KEY = 'career_advisor_pending_message';
const PENDING_MESSAGE_TYPE_KEY = 'career_advisor_pending_message_type';

export function useCareerChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<MessageType>('general');
  const [defaultAgent, setDefaultAgent] = useState<AIAgent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  // Fetch the default agent on component mount
  useEffect(() => {
    const fetchDefaultAgent = async () => {
      try {
        // Use maybeSingle instead of single to handle the case when no agent is found
        const { data, error } = await supabase
          .from('ai_agents')
          .select('*')
          .eq('is_default', true)
          .maybeSingle();

        if (error) {
          console.error("Error fetching default agent:", error);
          throw error;
        }

        // Set the default agent if found
        if (data) {
          setDefaultAgent(data as AIAgent);
        }
        
        // Add initial welcome message using the agent's name if available
        const agentName = data?.name || "AI Career Advisor";
        const agentExpertise = data?.expertise || "Get personalized guidance on career paths, skills, and opportunities";
        
        const initialMessage: Message = {
          id: generateId(),
          content: `👋 Hi there! I'm ${agentName}, your AI Career Advisor. I can help you discover career paths that match your interests and strengths.\n\nHow can I help you today? You can:\n\n• Ask general career questions\n• Share your resume for analysis\n• Share your LinkedIn profile for feedback\n• Discuss college and degree options if you're a student`,
          isUser: false,
          timestamp: new Date(),
          type: 'assistant'
        };

        setMessages([initialMessage]);
      } catch (error) {
        console.error("Error fetching default agent:", error);
        // Fallback to a generic message if we can't get the agent
        const fallbackMessage: Message = {
          id: generateId(),
          content: "👋 Hi there! I'm your AI Career Advisor. I can help you discover career paths that match your interests and strengths.\n\nHow can I help you today? You can:\n\n• Ask general career questions\n• Share your resume for analysis\n• Share your LinkedIn profile for feedback\n• Discuss college and degree options if you're a student",
          isUser: false,
          timestamp: new Date(),
          type: 'assistant'
        };
        setMessages([fallbackMessage]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaultAgent();
  }, []);

  // Check for pending message in localStorage when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      const pendingMessage = localStorage.getItem(PENDING_MESSAGE_KEY);
      const pendingMessageType = localStorage.getItem(PENDING_MESSAGE_TYPE_KEY) as MessageType || 'general';
      
      if (pendingMessage) {
        // Send the pending message
        handleSendMessage(pendingMessage, pendingMessageType);
        // Clear from localStorage after sending
        localStorage.removeItem(PENDING_MESSAGE_KEY);
        localStorage.removeItem(PENDING_MESSAGE_TYPE_KEY);
      }
    }
  }, [isAuthenticated]);

  const handleSendMessage = async (content: string, messageType: MessageType = 'general') => {
    if (!isAuthenticated) {
      // Save message to localStorage
      localStorage.setItem(PENDING_MESSAGE_KEY, content);
      localStorage.setItem(PENDING_MESSAGE_TYPE_KEY, messageType);
      setIsAuthOpen(true);
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: generateId(),
      content,
      isUser: true,
      timestamp: new Date(),
      type: 'user'
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    
    try {
      // Format messages for the Claude API
      const chatHistory = messages.map(msg => ({
        content: msg.content,
        isUser: msg.isUser
      }));

      // Call our Supabase Edge Function with agent data
      const { data, error } = await supabase.functions.invoke('chat-with-claude', {
        body: { 
          message: content,
          chatHistory: chatHistory,
          messageType: messageType,
          agent: defaultAgent
        }
      });

      if (error) {
        console.error("Error calling Edge Function:", error);
        toast({
          title: "Error",
          description: "Failed to get a response from the AI assistant. Please try again.",
          variant: "destructive"
        });
        throw error;
      }

      // Add AI response
      const aiMessage: Message = {
        id: data.id || generateId(),
        content: data.content,
        isUser: false,
        timestamp: new Date(),
        type: 'assistant'
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error in chat:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as MessageType);
  };

  const handleAuthSuccess = () => {
    setIsAuthOpen(false);
    // After successful authentication, the useEffect will detect and send any pending message
  };

  return {
    messages,
    isTyping,
    isAuthOpen,
    activeTab,
    defaultAgent,
    isLoading,
    setIsAuthOpen,
    handleSendMessage,
    handleTabChange,
    handleAuthSuccess
  };
}
